import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import apiClient from '../lib/apiClient';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1440
    );
    const { t, language, setLanguage } = useLanguage();

    useEffect(() => {
        apiClient.get('/api/user-session')
            .then(res => setUser(res.data.user))
            .catch(() => setUser(null));
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setIsOpen(false);
        } else {
            // If element not found (e.g., on Dashboard), go to home with hash
            window.location.href = `/#${id}`;
        }
    };

    const handleLogout = async () => {
        try {
            await apiClient.get('/auth/logout');
            window.location.href = '/';
        } catch (error) {
            console.error('Logout failed', error);
        }
    };

    const isMobile = viewportWidth <= 768;
    const isSmallMobile = viewportWidth <= 424;

    const styles = {
        nav: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #E5E5E5',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        },
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: isSmallMobile ? '0 14px' : isMobile ? '0 18px' : '0 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '72px',
            gap: '12px',
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            textDecoration: 'none',
            minWidth: 0,
            flexShrink: 1,
        },
        logoImg: {
            height: isSmallMobile ? '34px' : '40px',
            width: 'auto',
            flexShrink: 0,
        },
        logoText: {
            fontSize: isSmallMobile ? '18px' : isMobile ? '20px' : '24px',
            fontWeight: '800',
            color: '#3457D5',
            letterSpacing: '-0.5px',
            lineHeight: 1,
            whiteSpace: 'nowrap',
        },
        desktop: {
            display: 'flex',
            alignItems: 'center',
            gap: '40px',
        },
        navLink: {
            color: '#414141',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'color 0.3s',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textDecoration: 'none',
        },
        ctaButton: {
            padding: '12px 28px',
            backgroundColor: '#D81F26',
            color: '#FFFFFF',
            fontWeight: '600',
            borderRadius: '999px',
            fontSize: '14px',
            transition: 'all 0.3s',
            cursor: 'pointer',
            border: 'none',
        },
        mobileButton: {
            display: 'block',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            flexShrink: 0,
        },
        mobileMenu: {
            flexDirection: 'column',
            gap: '18px',
            padding: isSmallMobile ? '16px 14px 20px' : '18px 18px 24px',
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
        },
        mobileLink: {
            color: '#414141',
            fontSize: '15px',
            fontWeight: '500',
            textDecoration: 'none',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
        avatar: {
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#3457D5',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '14px',
        },
        langSelect: {
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            backgroundColor: '#fff',
            fontSize: '14px',
            color: '#414141',
            cursor: 'pointer',
            outline: 'none',
        }
    };

    return (
        <nav style={styles.nav}>
            <div style={styles.container}>
                <a href="/" style={styles.logoContainer}>
                    <img src="/corpusio-logo.webp" alt="Corpusio" style={styles.logoImg} />
                    <span style={styles.logoText}>Corpusio</span>
                </a>

                <div style={{ ...styles.desktop, display: isMobile ? 'none' : 'flex' }}>
                    <span style={styles.navLink} onClick={() => scrollToSection('products')}>{t('nav.products')}</span>
                    <span style={styles.navLink} onClick={() => scrollToSection('services')}>{t('nav.services')}</span>
                    <span style={styles.navLink} onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</span>
                    <span style={styles.navLink} onClick={() => scrollToSection('faq')}>{t('nav.faq')}</span>
                    <a href="/contact" style={styles.navLink}>{t('nav.contact')}</a>

                    <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        style={styles.langSelect}
                    >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                    </select>

                    {user ? (
                        <>
                            <a href="/dashboard" style={styles.navLink}>{t('nav.dashboard')}</a>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={styles.avatar}>
                                    {user.picture ? <img src={user.picture} alt="" style={{ ...styles.avatar, objectFit: 'cover' }} /> : user.name?.charAt(0)}
                                </div>
                                <span style={styles.navLink} onClick={handleLogout}>{t('nav.logout')}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <a href="/login" style={styles.navLink}>{t('nav.login')}</a>
                            <a href="/signup" style={{ textDecoration: 'none' }}>
                                <button style={styles.ctaButton}>{t('nav.signup')}</button>
                            </a>
                        </>
                    )}
                </div>

                <button
                    type="button"
                    aria-label={isOpen ? 'Close menu' : 'Open menu'}
                    style={{ ...styles.mobileButton, display: isMobile ? 'block' : 'none' }}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="#414141" viewBox="0 0 24 24">
                        {isOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </button>
            </div>

            {isOpen && isMobile && (
                <div style={{ ...styles.mobileMenu, display: 'flex' }}>
                    <span style={styles.mobileLink} onClick={() => scrollToSection('products')}>{t('nav.products')}</span>
                    <span style={styles.mobileLink} onClick={() => scrollToSection('services')}>{t('nav.services')}</span>
                    <span style={styles.mobileLink} onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</span>
                    <span style={styles.mobileLink} onClick={() => scrollToSection('faq')}>{t('nav.faq')}</span>
                    <a href="/contact" style={styles.mobileLink} onClick={() => setIsOpen(false)}>{t('nav.contact')}</a>

                    <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        style={{ ...styles.langSelect, width: '100%', padding: '12px' }}
                    >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                    </select>

                    {user ? (
                        <>
                            <a href="/dashboard" style={styles.mobileLink}>{t('nav.dashboard')}</a>
                            <span style={styles.mobileLink} onClick={handleLogout}>{t('nav.logout')}</span>
                        </>
                    ) : (
                        <>
                            <a href="/login" style={{ ...styles.mobileLink, display: 'block' }}>{t('nav.login')}</a>
                            <a href="/signup" style={{ textDecoration: 'none', display: 'block' }}>
                                <button style={{ ...styles.ctaButton, width: '100%' }}>{t('nav.signup')}</button>
                            </a>
                        </>
                    )}
                </div>
            )}
        </nav>
    );
};

export default Navbar;
